<template>
  <div class="wl-settings">
    <div class="row">
      <div class="col-12 col-md-6 col-lg-4">
        <h3>General Settings</h3>
        <b-form-group
          id="fieldset-customer-editable"
          description="Allow the customer to add / remove items and update selling prices on the wine list"
          v-if="isStaff"
        >
          <b-checkbox
            id="setting-customer-editable"
            v-model="form.customer_editable"
            value="1"
            unchecked-value="0"
            >Customer Editable?</b-checkbox
          >
        </b-form-group>

        <b-form-group
          id="fieldset-title"
          label="Title"
          label-for="setting-title"
        >
          <b-form-input
            id="setting-title"
            placeholder="Title"
            v-model="$v.form.list_title.$model"
            :state="validateState('list_title')"
          ></b-form-input>
        </b-form-group>

        <b-form-group
          id="fieldset-description"
          label="Description"
          description="This text will be visible when the wine list is shared"
          label-for="setting-description"
        >
          <b-form-textarea
            v-model="$v.form.list_description.$model"
            :state="validateState('list_description')"
            id="setting-description"
            placeholder="Public description (optional)..."
          ></b-form-textarea>
        </b-form-group>

        <b-form-group
          id="fieldset-customer-number"
          label="Customer Number"
          label-for="setting-customer-number"
        >
          <customer-selector
            v-model="$v.form.customer_details.$model"
            @input="setCustomerDefaults"
          ></customer-selector>
        </b-form-group>

        <b-form-group
          id="fieldset-company-name"
          label="Company Name"
          label-for="setting-company-name"
        >
          <b-form-input
            v-model="$v.form.company_name.$model"
            :state="validateState('company_name')"
            id="setting-company-name"
            placeholder="Company Name"
          ></b-form-input>
        </b-form-group>

        <b-form-group
          id="fieldset-contact-details"
          label="Contact Details"
          label-for="setting-contact-name"
        >
          <b-form-input
            class="mb-1"
            v-model="$v.form.customer_name.$model"
            :state="validateState('customer_name')"
            id="setting-contact-name"
            placeholder="Contact Name"
          ></b-form-input>
          <b-form-input
            class="mb-1"
            v-model="$v.form.customer_email.$model"
            :state="validateState('customer_email')"
            id="setting-contact-email"
            placeholder="Contact Email"
          ></b-form-input>
          <b-form-input
            class="mb-1"
            v-model="$v.form.customer_tel.$model"
            :state="validateState('customer_tel')"
            id="setting-contact-tel"
            placeholder="Contact Telephone"
          ></b-form-input>
        </b-form-group>

        <h4>Salesperson Details</h4>

        <p>{{ form.customer_details.SlpName }}</p>
        <p>{{ form.customer_details["AccountMgr Email"] }}</p>
        <p>{{ form.customer_details["AccountMgr Phone"] }}</p>
      </div>
      <div class="col-12 col-md-6 col-lg-4">
        <h3>Template Settings</h3>

        <b-form-group label="Enabled templates">
          <b-form-checkbox
            v-model="form.settings.templates"
            value="PRICE-YP"
            :disabled="!isStaff"
            >Price List - Invoice Prices</b-form-checkbox
          >
          <b-form-checkbox
            v-model="form.settings.templates"
            value="PRICE-SELL"
            :disabled="!isStaff"
            >Price List - Menu / Shelf Prices</b-form-checkbox
          >
          <b-form-checkbox
            v-model="form.settings.templates"
            value="PRICE-GLASS"
            :disabled="!isStaff"
            >Price List - By the Glass</b-form-checkbox
          >
          <b-form-checkbox v-model="form.settings.templates" value="BROCHURE"
            >Interactive Fact Sheet</b-form-checkbox
          >
          <b-form-checkbox v-model="form.settings.templates" value="TASTING"
            >Tasting Booklet</b-form-checkbox
          >
        </b-form-group>

        <b-form-group label="Group By">
          <b-form-select v-model="form.grouped_by">
            <b-form-select-option :value="null">None</b-form-select-option>
            <b-form-select-option value="colourtype"
              >Colour / Type</b-form-select-option
            >
            <b-form-select-option value="countryregion"
              >Country / Region</b-form-select-option
            >
          </b-form-select>
        </b-form-group>

        <b-form-group label="Order By">
          <b-form-select v-model="form.sorted_by">
            <b-form-select-option value="vintageprice"
              >Vintage, Price (Low to High)</b-form-select-option
            >
            <b-form-select-option value="price"
              >Price (Low to High)</b-form-select-option
            >
          </b-form-select>
        </b-form-group>

        <b-checkbox
          id="setting-reset-sorting"
          v-model="form.reset_sorting"
          value="1"
          >Reset custom ordering?</b-checkbox
        >
      </div>
      <div class="col-12 col-md-6 col-lg-4" v-if="isStaff">
        <h3>Pricing</h3>
        <b-form-group label="Universal Discount">
          <b-input-group append="%">
            <b-form-input
              v-model="$v.form.discount.$model"
              :state="validateState('discount')"
              type="number"
              no-wheel
            ></b-form-input>
          </b-input-group>
        </b-form-group>
      </div>
    </div>
  </div>
</template>

<script>
import CustomerSelector from "@/components/CustomerSelector";

import {
  required,
  minLength,
  maxLength,
  email,
  decimal,
} from "vuelidate/lib/validators";

export default {
  components: {
    CustomerSelector,
  },
  props: {
    value: {
      type: [Object, Boolean],
      default: false,
    },
  },
  watch: {
    value(val) {
      this.data_incoming = true;
      this.form = { ...val };
      this.$v.$touch();
      if (this.$v.$invalid) {
        this.$emit("valid", false);
      } else {
        this.$emit("valid", true);
      }
    },
    form: {
      deep: true,
      handler() {
        if (!this.data_incoming) {
          this.emitValidity();
        }
        this.data_incoming = false;
      },
    },
  },
  data() {
    return {
      form: {
        list_title: "",
        list_description: "",
        company_name: "",
        customer_number: "",
        customer_name: "",
        customer_email: "",
        customer_tel: "",
        customer_details: {},
        settings: {
          templates: [],
        },
        discount: 0,
        grouped_by: null,
        sorted_by: "vintageprice",
      },
    };
  },
  mounted() {
    if (this.value !== false) {
      this.form = { ...this.value };
      this.$v.$touch();
      this.emitValidity();
    } else {
      this.$v.form.company_name.$model = this.$store.state.user.customer_name;
      this.$v.form.customer_number.$model = Object.keys(
        this.$store.state.user.customers
      )[0];
    }
  },
  methods: {
    setCustomerDefaults(val) {
      this.$v.form.company_name.$model = val.CardName;
      this.$v.form.customer_name.$model = val.SlpName;
      this.$v.form.customer_email.$model = val['AccountMgr Email'];
      this.$v.form.customer_tel.$model = val['AccountMgr Phone'];
    },
    validateState(name) {
      const { $dirty, $error } = this.$v.form[name];
      return $dirty ? !$error : null;
    },
    emitValidity() {
      if (this.$v.$invalid) {
        this.$emit("valid", false);
      } else {
        this.$emit("valid", true);
        this.form.customer_number = this.form.customer_details.CardCode;
        this.$emit("input", this.form);
      }
    },
  },
  validations() {
    var rules = {
      form: {
        list_title: {
          required,
          maxLength: maxLength(100),
        },
        list_description: {},
        company_name: {
          required,
          maxLength: maxLength(100),
        },
        customer_number: {
          required,
        },
        customer_name: {
          required,
          maxLength: maxLength(100),
        },
        customer_email: {
          required,
          email,
          maxLength: maxLength(100),
        },
        customer_tel: {
          required,
          maxLength: maxLength(20),
          minLength: minLength(6),
        },
        customer_details: {
          required,
        },
      },
    };

    if (this.isStaff) {
      rules.form.discount = {
        required,
        decimal,
      };
    }

    return rules;
  },
};
</script>